import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import Axios from 'axios';
import { useHistory } from "react-router-dom";
import { FormattedMessage } from 'react-intl';

import './Slide.css';
import { StepContext } from '../contexts/StepContext';
import { ChronoContext } from '../contexts/ChronoContext';
import { ButtonNext } from '../contexts/ButtonNext';
import { LanguageContext } from '../contexts/LanguageContext';

import PageStart from './Slides/PageStart';
import Informations from './Slides/Informations';
import PageRadioButton from './Slides/PageRadioButton';
import PageCB from './Slides/PageCB';
import PageTraitsPersonnalite from './Slides/PageTraitsPersonnalite';
import PageSituationStrategie from './Slides/PageSituationStrategie';
import PageMultiSlider from './Slides/PageMulitSlider';
import Results from './Slides/Results';

import messages from '../lang/preferences';
import url from '../constants/urlsPaths';

import ppeLogo from '../ressources/logo-sq-green.svg';

// Helper function to safely get the auth token from session storage
const getAuthToken = () => {
    const token = sessionStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") {
        return null;
    }
    try {
        return JSON.parse(token);
    } catch (e) {
        console.error("Failed to parse auth token:", e);
        return null;
    }
};




export default function Slide({ questions, token }) {
    // --- Hooks ---
    const { step, setStep } = useContext(StepContext);
    const { chrono, setChrono } = useContext(ChronoContext);
    const { buttonNext, setButtonNext } = useContext(ButtonNext);
    const { selectLanguage } = useContext(LanguageContext);
    const [loading, setLoading] = useState(true);
    const [demographics, setDemographics] = useState(false);
    const history = useHistory();


    const fetchAndSetUserAnswers = useCallback(() => {
        const uc = sessionStorage.getItem("code");
        const ut = getAuthToken();
        Axios.post(url.getAnswers, { code: uc }, {
            headers: { "x-access-token": ut }
        }).then(res => {
            const answersArr = res.data.answers || [];
            //const foundUserAnswers = answersArr.find(a => String(a.code) === String(userCode));
            //console.log("Response user answers...", step);

            const getValueForKey = (arr, key) => {
                const obj = arr.find(a => Object.prototype.hasOwnProperty.call(a, key));
                return obj ? obj[key] : "";
            };

            const sexe = getValueForKey(answersArr, "SEXE");
            const age = getValueForKey(answersArr, "AGE");
            const ecole = getValueForKey(answersArr, "ECOLE");
            const continent = getValueForKey(answersArr, "CONTINENT");
            const poste = getValueForKey(answersArr, "POSTE");

            if (
                answersArr.length > 0 &&
                sexe &&
                age &&
                ecole &&
                continent &&
                poste
            ) {
                setDemographics(true);

                let existingAnswers = [];
                try {
                    existingAnswers = JSON.parse(sessionStorage.getItem("answers")) || [];
                } catch (e) {
                    existingAnswers = [];
                }
                const upsertKeyValue = (arr, key, value) => {
                    const idx = arr.findIndex(obj => Object.prototype.hasOwnProperty.call(obj, key));
                    if (idx !== -1) {
                        arr[idx][key] = value;
                    } else {
                        arr.push({ [key]: value });
                    }
                };

                upsertKeyValue(existingAnswers, "SEXE", sexe);
                upsertKeyValue(existingAnswers, "AGE", age);
                upsertKeyValue(existingAnswers, "ECOLE", ecole);
                upsertKeyValue(existingAnswers, "CONTINENT", continent);
                upsertKeyValue(existingAnswers, "POSTE", poste);
                sessionStorage.setItem("demographics", true);
                sessionStorage.setItem("answers", JSON.stringify(existingAnswers));
                //console.log("AFTER SET storedAnswers:", sessionStorage.getItem("answers"));
            } else {
                console.log("❌ Demographics not complete, setting demographics to false");
                setDemographics(false);
            }
        }).catch(err => {
            console.log("Fetching user answers failed:", err);
            setDemographics(false);
        });
    }, []);

    // Refs for managing timer state without causing re-renders
    const timerId = useRef(null);
    const chronoRef = useRef({ hours: 0, minutes: 0, seconds: 0 });

    // --- Memoized Callbacks ---

    // Stops the timer
    const stopChrono = useCallback(() => {
        console.log("Chrono stopped");
        clearTimeout(timerId.current);
    }, []);

    // Increments the timer every second
    const add = useCallback(() => {
        const time = chronoRef.current;
        time.seconds++;
        if (time.seconds >= 60) {
            time.seconds = 0;
            time.minutes++;
            if (time.minutes >= 60) {
                time.minutes = 0;
                time.hours++;
            }
        }
        setChrono({ ...time }); // Update context to display new time
        timerId.current = setTimeout(add, 1000); // Schedule next tick
    }, [setChrono]);

    // Starts the timer
    const startChrono = useCallback(() => {
        clearTimeout(timerId.current); // Ensure no multiple timers are running
        timerId.current = setTimeout(add, 1000);
        fetchAndSetUserAnswers(); // check for any demographic data
    }, [add, fetchAndSetUserAnswers]);

    // Sends notification to admin when the test is completed
    const sendTestCompletionNotification = useCallback(async () => {
        try {          
            const uc = sessionStorage.getItem("code");
            const ut = getAuthToken();
            if (!uc || !ut) return;

            //console.log("🔔 Sending test completion notification..."); 
            await Axios.post(url.adminEmailNotification, {
                userCode: uc,
                testCompleted: true,
                testDuration: `${chrono.hours}h ${chrono.minutes}m ${chrono.seconds}s`,
                timestamp: new Date().toISOString()
            }, {
                headers: { "x-access-token": ut }
            });
            //console.log("✅ Admin notification sent successfully.");
        } catch (error) {
           // console.error("❌ Failed to send admin notification:", error);
        }
    }, [chrono]);

    useEffect(() => {
        if (!token) {
            history.push('/');
            return;
        }

        Axios.post(url.survey, { code: sessionStorage.getItem("code") }, {
            headers: { "x-access-token": getAuthToken() }
        })
        .then(res => {
            if (!res.data.auth) {
                sessionStorage.clear();
                window.location.reload();
            } else if (res.data.testPassed && res.data.type !== "admin") {
                history.push('/results');
            } else {
                // Restore state from sessionStorage if page was reloaded
                if (sessionStorage.getItem("language")) {
                    selectLanguage(sessionStorage.getItem("language"));
                }
                const savedStep = parseInt(sessionStorage.getItem("step") || "0");
                if (savedStep > 0) {
                    setStep(savedStep);
                    const savedChrono = JSON.parse(sessionStorage.getItem("chrono") || 'null');
                    if (savedChrono) {
                        chronoRef.current = savedChrono;
                        setChrono(savedChrono);
                        startChrono();
                    }
                }
                setLoading(false); // Show slide content and button immediately
            }
        })
        .catch(err => {
            console.error("Survey fetch error:", err);
            setLoading(false);
        });
    }, [history, selectLanguage, setChrono, setStep, startChrono, token]);


    // --- Event Handlers ---

    const nextSlide = () => {
        let newStep = step + 1;
        const nbMaxQuestions = questions.length;
        if (demographics && newStep === nbMaxQuestions - 8) {
            //console.log("existing answers:", sessionStorage.getItem("answers"));
        }

            // If demographics is true and we're at the step before the last 6, skip to the last step
        if (demographics && newStep === nbMaxQuestions - 7) {
            newStep = nbMaxQuestions - 1;
        }

        if (newStep >= nbMaxQuestions) {
            alert("Erreur: Fin du questionnaire atteinte.");
            return;
        }

        setStep(newStep);
        sessionStorage.setItem('step', newStep.toString());
        sessionStorage.setItem('chrono', JSON.stringify(chrono));
        //console.log(`Next slide: ${newStep} / ${nbMaxQuestions}`, chrono, timerId.current);

        if (newStep === 1) { // Start timer on the first "next" click
            startChrono();
        }

        if (newStep === nbMaxQuestions - 1) { // Last question slide
            stopChrono();
            sendTestCompletionNotification();
            setTimeout(() => {
                window.location.href = "/results";
            }, 300);
        }

        setButtonNext(true);
    };

    // --- Render Logic ---

    const renderSlideType = (type) => {
        switch (type) {
            case 'results': return <Results slide={questions} />;
            case 'pageStart': return <PageStart slide={questions[step]} />;
            case 'informations': return <Informations slide={questions[step]} />;
            case 'pageRadioButton': return <PageRadioButton slide={questions[step]} />;
            case 'pageRadioFO': {
                const PageRadioFO = require('./Slides/PageRadioFO').default;
                return <PageRadioFO slide={questions[step]} />;
            }
            case 'pageSituationStrategy': return <PageSituationStrategie slide={questions[step]} />;
            case 'pageCheckBox': return <PageCB slide={questions[step]} />;
            case 'pageMultiSlider': return <PageMultiSlider slide={questions[step]} />;
            case 'pageTraitsPersonnalite': return <PageTraitsPersonnalite slide={questions[step]} />;
            default:
                console.warn(`Slide type "${type}" not found.`);
                return null;
        }
    };

    if (loading) {
        return (
            <div className="Slide-loading-overlay">
                <h3><FormattedMessage {...messages.LOADING_PAGE} /></h3>
            </div>
        );
    }

    // Debug: Show the slide object being rendered
    const currentSlide = questions[step];

    return (
        <div>
        <div className="Slide">
            <img alt='ppe-logo' className="login-logo" src={ppeLogo}></img> 
            <div className="Slide-content">
                { /* End of test - answers sent to backend */ }
                {step < questions.length && (
                    <div className="Slide-question">
                        {renderSlideType(currentSlide?.type)}
                    </div>
                )}
            </div>
          
        </div>
          {step < questions.length - 1 &&
                    <div className="Slide-footer">
                        <button 
                            className="Slide-btn-next" 
                            disabled={buttonNext} 
                            onClick={nextSlide}
                        >
                            <FormattedMessage {...messages.BTNSUIVANT} />
                        </button>
                    </div>
                }
        </div>
    );
}